import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { flushUserSettings, useSettingsStore } from '@/api/bi/settings';

/**
 * Auto-save status for preference screens.
 *
 * Settings are written with a debounced PATCH and no explicit Save button. That
 * is the right interaction, but it only works if failure is visible: the old
 * version rendered a bare "Saving…" label and nothing at all when the request
 * failed, so a rejected write looked identical to a successful one until the
 * preference reappeared unchanged on another device.
 */
export function SavingIndicator() {
  const saving = useSettingsStore((s) => s.saving);
  const saveError = useSettingsStore((s) => s.saveError);
  const savedAt = useSettingsStore((s) => s.savedAt);

  if (saveError) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-danger">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <Tooltip content={saveError}>
          <span className="cursor-help underline decoration-dotted">Not saved to your account</span>
        </Tooltip>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-1.5 text-[11px]"
          onClick={() => void flushUserSettings()}
        >
          Retry
        </Button>
      </span>
    );
  }

  if (saving) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Saving…
      </span>
    );
  }

  if (savedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Check className="h-3.5 w-3.5 text-success" aria-hidden /> Saved
      </span>
    );
  }

  return null;
}
